import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useCallback,
    useEffect,
} from "react";
import { appRoutes } from "../../constants/appRoutes";
import { useLocation } from "react-router-dom";

export interface LienProcessFilters {
    customerFilter?: string[];
    jobFilter?: string[];
    monthFilter?: string[];
    page?: number | string;
}

interface LienProcessFilterContextType {
    filters: LienProcessFilters;
    updateFilters: (params: Partial<LienProcessFilters>) => void;
    resetFilters: () => void;
}

const LienProcessFilterContext = createContext<
    LienProcessFilterContextType | undefined
>(undefined);

export const useLienProcessFilterContext =
    (): LienProcessFilterContextType => {
        const context = useContext(LienProcessFilterContext);
        if (!context) {
            throw new Error(
                "useLienProcessFilterContext must be used within a LienProcessFilterContextProvider"
            );
        }
        return context;
    };

export const LienProcessFilterContextProvider: React.FC<{
    children: ReactNode;
}> = ({ children }) => {
    const location = useLocation();
    const [filters, setFilters] = useState<LienProcessFilters>({});

    const updateFilters = useCallback((params: Partial<LienProcessFilters>) => {
        setFilters((prevState) => {
            return { ...prevState, ...params };
        });
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({});
    }, []);

    useEffect(() => {
        if (
            location &&
            !location?.pathname?.startsWith(appRoutes.LIEN_PROCESS)
        ) {
            resetFilters();
        }
    }, [location?.pathname, resetFilters]);

    return (
        <LienProcessFilterContext.Provider
            value={{
                filters,
                updateFilters,
                resetFilters,
            }}
        >
            {children}
        </LienProcessFilterContext.Provider>
    );
};
